import { Col, Flex, Input, Row, Table, Typography,Card,Button } from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SearchOutlined,MessageOutlined } from "@ant-design/icons";
import { appConstConfig } from "../../../../../constants/config/appConstConfig";
import { colors } from "../../../../../constants/colors";
import useDebounce from "../../../../../hooks/useDebounce";
import styles from "./ServiceProviderReviewInfo.module.scss";
import {
  getReviewsAction,
  getServiceProviderReviewsAction,
} from "../../../../../redux/services/reviews/reviewsServices";
import { routes, userRoles } from "../../../../../constants";
import Actions from "../../../../../redux/actions";
import Spinner from "../../../../common/spinner/Spinner";
import { dateObjToDispViewDate,timeObjToDispViewTimeWithSuffix } from "../../../../../utils/utils";
import { baseRouteMaker } from "../../../../../constants/routeConsts";
import { appConfig } from "../../../../../constants/config/appConfig";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import ReviewInfoDialog from "./ReviewInfoDialog";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.guess();
const ServiceProviderReviewInfo = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  const { userSession } = useLocalStorage(appConfig.SESSION_USER_INFO);

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { serviceProviderReviews } = useSelector((state) => state.reviews);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: serviceProviderReviews?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  const [
    selectedServiceProviderReviewInfoDetails,
    setSelectedServiceProviderReviewInfoDetails,
  ] = useState(null);
  /* table dependencies */

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );

  /* dialog dependencies */
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  /* dialog dependencies */
  const [reviewDialogData, setReviewDialogData] = useState({});

  /* function definition to get table data */
  const handleGetTableData = () => {
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
    };
    if (userSession?.userRole !== userRoles.SERVICE_PROVIDER_ADMIN)
      dataToBeSent["service_provider_id"] = userSession?.userId;
    dispatch(getServiceProviderReviewsAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  const handleReviewDetails = (e, row) => {
    setOpenReviewDialog(true);
    setReviewDialogData(row);
  };

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    let tempTableColumns = [
      {
        title: t("reviewInfo.orderId"),
        dataIndex: "orderId",
        render: (_, record) => record?.order_number,
      },
      {
        title: t("reviewInfo.ReviewDate"),
        dataIndex: "appointmentDate",
        key: "created_at",
        render: (_, record) => (
          <p>{`${dateObjToDispViewDate(record?.created_at, "ddmmyyyy")} - ${timeObjToDispViewTimeWithSuffix(dayjs
              .utc(record?.created_at).local().tz(dayjs.tz.guess()).format("YYYY-MM-DDTHH:mm:ss")
          )}`}</p>
        ),
      },
      {
        title: t("reviewInfo.customerName"),
        render: (_, record) =>
          record?.user?.first_name || record?.user?.last_name
            ? `${record?.user?.first_name} ${record?.user?.last_name}`
            : "-",
      },
      {
        title: t("reviewInfo.serviceProviderName"),
        dataIndex: "serviceProivderName",
        render: (_, record) =>
          record?.service_provider
            ? `${record.service_provider?.first_name} ${record.service_provider?.last_name}`
            : "",
      },
      {
        title: t("reviewInfo.headline"),
        dataIndex: "review",
        key: "review",
        render: (_, record) => (
          <div
            style={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "150px",
              overflow: "hidden",
            }}
          >
            {record?.review || ""}
          </div>
        ),
      },
      {
        title: t("reviewInfo.rating"),
        dataIndex: "rating",
        key: "rating",
      },
      {
        title: t("reviewInfo.detailedReview"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                {t("viewText")}
              </Typography.Link>
            </>
          );
        },
      },
    ];
    /* table column config */
    if (userSession.userRole === userRoles.SERVICE_PROVIDER_ADMIN) {
      tempTableColumns = [
        ...tempTableColumns,
        {
          title: t("CommonUtils.leaveAReview"),
          key: "action2",
          align: "center",
          render: (_, record) => {
            return (
              <>
                <Typography.Link
                  onClick={(e) => handleReviewDetails(e, record)}
                >
                  {t("CommonUtils.Review")}
                </Typography.Link>
              </>
            );
          },
        },
      ];
    }
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleViewDetails = (e, row) => {
    navigate(baseRouteMaker(routes.SERVICE_PROVIDER_REVIEW_INFO_DETAILS));
    dispatch(
      Actions.createAction(
        Actions.SELECTED_SERVICE_PROVIDER_REVIEW_INFO_DETAILS,
        { selectedServiceProviderReviewInfoDetails: row }
      )
    );
  };

  return (
    <div className={styles.reviewInfoContainer}>
      <Flex vertical gap="2rem">
        <Row gutter={[24]}  justify="space-between">
          <Col span={7} >
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder="Search"
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </Col>
          <Col span={8}>
          <Card
                  bordered
                  hoverable
                  style={{
                    width: 300,
                    padding: 0,
                    borderRadius: 20,
                    border: `1px solid ${colors.oceanBlue}`,
                  }}
                >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={6}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<MessageOutlined style={{ fontSize: 22 }} />}
                    style={{ background: colors.oceanBlue }}
                  />
                </Col>
                <Col span={18}>
                  <Flex vertical>
                    <Typography.Title level={3}>
                      {t("reviewInfo.reviews")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.DarkMint }}
                    >
                      {`${serviceProviderReviews?.total || 0}`}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
          </Card>
          </Col>
        </Row>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={serviceProviderReviews?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>

      <ReviewInfoDialog
        open={openReviewDialog}
        reviewDialogData={reviewDialogData}
        onClose={() => {
          setOpenReviewDialog(false);
          handleGetTableData();
          setReviewDialogData({});
        }}
      />
    </div>
  );
};

export default ServiceProviderReviewInfo;
